import { Fragment, useCallback } from 'react';
import { getCountriesByRegion } from '@final-commerce/common';
import { Select } from '@/ui/Select/Select';
import {
  COUNTRY_LABELS,
  PRICING_REGION_LABELS,
  PRICING_REGION_ORDER,
  PricingRegion,
} from '@/constants/pricingCountries';

export type CountryOption = {
  code: string;
  label: string;
  region: PricingRegion;
  disabled: boolean;
};

type Props = {
  value?: string;
  onChange: (countryCode: string) => void;
  options: CountryOption[];
  label?: string;
  disabled?: boolean;
};

export const buildCountryOptions = (disabledCountries: string[] = []): CountryOption[] => {
  const disabled = new Set(disabledCountries);
  return PRICING_REGION_ORDER.flatMap((region) =>
    [...getCountriesByRegion(region)].map((code) => ({
      code,
      label: COUNTRY_LABELS[code] ?? code,
      region,
      disabled: disabled.has(code),
    })),
  );
};

const renderOption = (
  raw: unknown,
  onClick: (value: string) => void,
  options: CountryOption[],
): React.ReactNode => {
  const option = raw as CountryOption;
  const indexInRegion = options
    .filter((o) => o.region === option.region)
    .findIndex((o) => o.code === option.code);
  const showHeader = indexInRegion === 0;

  return (
    <Fragment key={option.code}>
      {showHeader && (
        <li
          className='px-3 py-1 text-xs font-semibold uppercase tracking-wide text-muted-foreground bg-popover'
          aria-hidden='true'
        >
          {PRICING_REGION_LABELS[option.region]}
        </li>
      )}
      <li
        className={
          option.disabled
            ? 'text-left bg-popover px-3 py-2 cursor-not-allowed opacity-40'
            : 'text-left bg-popover px-3 py-2 hover:bg-accent hover:text-accent-foreground cursor-pointer'
        }
        onClick={() => !option.disabled && onClick(option.code)}
      >
        <span className='text-sm font-light'>{option.label}</span>
      </li>
    </Fragment>
  );
};

export const CountrySelect = ({ value, onChange, options, label, disabled }: Props) => {
  const displayLabel = value ? (COUNTRY_LABELS[value] ?? value) : (label ?? 'Select a country');

  const renderCountryOption = useCallback(
    (opt: unknown, onClick: (value: string) => void) => renderOption(opt, onClick, options),
    [options],
  );

  return (
    <Select
      value={displayLabel}
      onChange={onChange}
      options={options}
      disabled={disabled}
      renderOption={renderCountryOption}
    />
  );
};

export default CountrySelect;
